/**
 * @file PlayerResultItemContainer.tsx
 * @description Container component that handles logic for PlayerResultItem
 */
import React, { memo, useCallback, useMemo } from "react";

import { PlayerSearchPlayerFragment, Position } from "@/generated/graphql";
import { getPositionAndTeamName } from "@/utils/getPlayerInformations";

import PlayerResultItemView from "./PlayerResultItemView";

/**
 * Props for PlayerResultItemContainer component
 */
export interface PlayerResultItemProps {
  readonly player: PlayerSearchPlayerFragment;
  readonly onSelect: (playerId: string, playerName?: string) => void;
  readonly isDark: boolean;
}

/**
 * Container component that processes player data and handles selection
 */
const PlayerResultItemContainer = memo(function PlayerResultItemContainer({
  player,
  onSelect,
  isDark,
}: PlayerResultItemProps) {
  const playerName = useMemo(() => player?.name ?? "", [player]);

  const handlePress = useCallback(() => {
    onSelect(player.id, playerName);
  }, [onSelect, player.id, playerName]);

  const positionAndTeam = useMemo(
    () =>
      getPositionAndTeamName(
        (player.positions ?? []) as Position[],
        player.team?.name ?? player.teamName ?? undefined,
        false
      ),
    [player.positions, player.team?.name, player.teamName]
  );

  const isManual = player.isManual ?? false;

  return (
    <PlayerResultItemView
      playerName={playerName}
      positionAndTeam={positionAndTeam}
      isManual={isManual}
      onPress={handlePress}
      isDark={isDark}
    />
  );
});

PlayerResultItemContainer.displayName = "PlayerResultItemContainer";

export default PlayerResultItemContainer;
